import camelCase from 'lodash/camelCase'
import startCase from 'lodash/startCase'
import React, { useEffect, useMemo, useState } from 'react'

import { CalculatorCityAutocomplete } from '../components/calculator/CalculatorCityAutocomplete'
import { CalculatorDateFields } from '../components/calculator/CalculatorDateFields'
import { CalculatorStateField } from '../components/calculator/CalculatorStateField'
import { Footer } from '../components/Footer'
import { Spinner } from '../components/Spinner'
import { TopLogo } from '../components/TopLogo'
import { useCalculatorForm } from '../hooks/useCalculatorForm'
import { IncomeCalcDTO } from '../models/IncomeCalcDTO.model'
import { months } from '../models/Months.model'
import { USStates } from '../models/USStates.model'
import { getObjectKeyFromValue, scrollIntoView } from '../utils'
import {
  blockNegativeNumberKeys,
  getIncomeCalcErrorMessage,
  isAllowedNonNegativeNumericInput,
  isNegativeNumericValue,
} from '../utils/calculatorErrors'
import { calculateCompensation } from '../workers/compensationCalc'

type CalculatorResultContext = {
  city: string
  startDay: number
  startMonth: number
  startYear: number
  state: USStates
}

const formatCalculatorResultContext = (context: CalculatorResultContext): string => {
  const stateKey = getObjectKeyFromValue(USStates, context.state)
  const stateName = stateKey ? startCase(camelCase(stateKey)) : String(context.state)
  const monthName = months[context.startMonth - 1]

  return `${context.city}, ${stateName} — ${monthName} ${context.startDay}, ${context.startYear}`
}

type IncomeFieldValues = {
  'hourly-rate'?: string
  'hour-amount'?: string
  'overtime-rate'?: string
  'overtime-amount'?: string
  'contracted-weekly-stipends'?: string
}

const NUMERIC_FIELDS = [
  { key: 'hourly-rate', label: 'Hourly Rate' },
  { key: 'hour-amount', label: 'Number of Normal Hours' },
  { key: 'overtime-rate', label: 'Overtime Rate' },
  { key: 'overtime-amount', label: 'Number of Overtime Hours' },
  { key: 'contracted-weekly-stipends', label: 'Contract Weekly Stipends' },
] as const

const IncomeCalc: React.FC = () => {
  const calculatorForm = useCalculatorForm()
  const [isLoading, setIsLoading] = useState(false)
  const [submitErrorMessage, setSubmitErrorMessage] = useState('')
  const [incomeValues, setIncomeValues] = useState<IncomeFieldValues>({})
  const [incomeValidationErrors, setIncomeValidationErrors] = useState<Record<string, string>>({})
  const [showCalculation, setShowCalculation] = useState(false)
  const [lastGsaResult, setLastGsaResult] = useState<IncomeCalcDTO | undefined>()
  const [resultContext, setResultContext] = useState<CalculatorResultContext | undefined>()

  const {
    fieldValues,
    validMonths,
    validYears,
    validDays,
    locations,
    filteredLocations,
    locationsLoading,
    locationsError,
    validationErrors,
    isDateValid,
    isCityValid,
    canSubmit,
    resolvedCity,
    validateLocationForm,
    updateMonth,
    updateDay,
    updateYear,
    updateState,
    updateCityQuery,
    selectCity,
    incomeCalcService,
  } = calculatorForm

  const hasNegativeIncomeValues = NUMERIC_FIELDS.some(({ key }) => isNegativeNumericValue(incomeValues[key]))

  useEffect(() => {
    setShowCalculation(false)
    setLastGsaResult(undefined)
    setResultContext(undefined)
  }, [
    fieldValues.state,
    fieldValues.startMonth,
    fieldValues.startDay,
    fieldValues.startYear,
    fieldValues.cityQuery,
    fieldValues.selectedCity,
  ])

  const calculation = useMemo(() => {
    if (!showCalculation || !lastGsaResult) {
      return undefined
    }

    return calculateCompensation({
      GSAResult: lastGsaResult,
      hourAmount: Number(incomeValues['hour-amount'] || 0),
      hourlyRate: Number(incomeValues['hourly-rate'] || 0),
      numberOfDays: 7,
      overtimeAmount: Number(incomeValues['overtime-amount'] || 0),
      overtimeRate: Number(incomeValues['overtime-rate'] || 0),
      startMonth: fieldValues.startMonth,
    })
  }, [showCalculation, lastGsaResult, incomeValues, fieldValues.startMonth])

  const contractedWeeklyStipends = incomeValues['contracted-weekly-stipends']
    ? Number(incomeValues['contracted-weekly-stipends'])
    : 0

  const updateIncomeField = (key: keyof IncomeFieldValues, value: string) => {
    if (!isAllowedNonNegativeNumericInput(value)) {
      return
    }

    setIncomeValues((prev) => ({ ...prev, [key]: value }))
    setIncomeValidationErrors((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  const submitCalculator = () => {
    setShowCalculation(false)
    setLastGsaResult(undefined)
    setResultContext(undefined)
    setSubmitErrorMessage('')

    const nextIncomeErrors: Record<string, string> = {}
    NUMERIC_FIELDS.forEach(({ key, label }) => {
      if (isNegativeNumericValue(incomeValues[key])) {
        nextIncomeErrors[key] = `${label} must be zero or greater.`
      }
    })
    setIncomeValidationErrors(nextIncomeErrors)

    const isLocationValid = validateLocationForm()
    if (!isLocationValid || Object.keys(nextIncomeErrors).length > 0) {
      const validationElements = document.getElementsByClassName('validation-error')
      if (validationElements.length > 0) {
        ;(validationElements[0] as HTMLElement)?.focus()
      }
      return
    }

    const city = resolvedCity || fieldValues.selectedCity
    if (!city) {
      return
    }

    setIsLoading(true)
    incomeCalcService
      .get({
        city,
        startDay: fieldValues.startDay,
        startMonth: fieldValues.startMonth,
        startYear: fieldValues.startYear,
        state: fieldValues.state,
      })
      .then((GSAResult) => {
        setResultContext({
          city,
          startDay: fieldValues.startDay,
          startMonth: fieldValues.startMonth,
          startYear: fieldValues.startYear,
          state: fieldValues.state,
        })
        setLastGsaResult(GSAResult)
        setShowCalculation(true)
        setIsLoading(false)

        const focusTrapElement = document.getElementsByClassName('calculator-focus-trap')
        if (focusTrapElement.length > 0) {
          ;(focusTrapElement[0] as HTMLElement)?.focus()
        }
      })
      .catch((err) => {
        const { message, field } = getIncomeCalcErrorMessage(err)
        if (field) {
          calculatorForm.setValidationErrors((prev) => ({ ...prev, [field]: message }))
        } else {
          setSubmitErrorMessage(message)
        }
        setIsLoading(false)
        console.error(err)
      })
  }

  return (
    <div id="page-root" className="relative flex h-screen flex-col overflow-auto">
      <TopLogo />
      <div className="mx-auto w-full max-w-4xl flex-1 px-4 py-6 sm:py-8">
        <div>
          <div className="mb-8 text-center sm:mb-10">
            <h1 className="mb-2 text-2xl font-bold text-graphite-grey sm:text-3xl">Income Calculator</h1>
            <p className="text-sm text-gray-600 sm:text-base">Get the most out of your contract</p>
          </div>

          <div className="mb-8 rounded-lg bg-white p-5 shadow-lg sm:p-6 md:p-8">
            <h2 className="mb-4 text-lg font-bold text-graphite-grey sm:text-xl">Enter Your Details</h2>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">
              <CalculatorDateFields
                startMonth={fieldValues.startMonth}
                startDay={fieldValues.startDay}
                startYear={fieldValues.startYear}
                validMonths={validMonths}
                validYears={validYears}
                validDays={validDays}
                validationError={validationErrors['start-date']}
                onMonthChange={updateMonth}
                onDayChange={updateDay}
                onYearChange={updateYear}
              />

              <div>
                <label className="mb-1.5 block text-xs font-semibold text-graphite-grey sm:text-sm">
                  Hourly Rate ($)
                </label>
                <input
                  type="number"
                  step=".01"
                  min="0"
                  name="hourly-rate"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-royal-indigo sm:px-4"
                  placeholder="0.00"
                  value={incomeValues['hourly-rate'] ?? ''}
                  onChange={(evt) => updateIncomeField('hourly-rate', evt.target.value)}
                  onKeyDown={(evt) => {
                    blockNegativeNumberKeys(evt)
                    if (evt.key === 'Enter') {
                      submitCalculator()
                    }
                  }}
                  onFocus={(evt) => scrollIntoView(evt.target)}
                />
                {incomeValidationErrors['hourly-rate'] && (
                  <div className="validation-error mt-1 text-sm text-red-500" tabIndex={-1}>
                    {incomeValidationErrors['hourly-rate']}
                  </div>
                )}
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold text-graphite-grey sm:text-sm">
                  Number of Normal Hours
                </label>
                <input
                  type="number"
                  step="any"
                  min="0"
                  name="hour-amount"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-royal-indigo sm:px-4"
                  placeholder="0"
                  value={incomeValues['hour-amount'] ?? ''}
                  onChange={(evt) => updateIncomeField('hour-amount', evt.target.value)}
                  onKeyDown={(evt) => {
                    blockNegativeNumberKeys(evt)
                    if (evt.key === 'Enter') {
                      submitCalculator()
                    }
                  }}
                  onFocus={(evt) => scrollIntoView(evt.target)}
                />
                {incomeValidationErrors['hour-amount'] && (
                  <div className="validation-error mt-1 text-sm text-red-500" tabIndex={-1}>
                    {incomeValidationErrors['hour-amount']}
                  </div>
                )}
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold text-graphite-grey sm:text-sm">
                  Overtime Rate ($)
                </label>
                <input
                  type="number"
                  step=".01"
                  min="0"
                  name="overtime-rate"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-royal-indigo sm:px-4"
                  placeholder="0.00"
                  value={incomeValues['overtime-rate'] ?? ''}
                  onChange={(evt) => updateIncomeField('overtime-rate', evt.target.value)}
                  onKeyDown={(evt) => {
                    blockNegativeNumberKeys(evt)
                    if (evt.key === 'Enter') {
                      submitCalculator()
                    }
                  }}
                  onFocus={(evt) => scrollIntoView(evt.target)}
                />
                {incomeValidationErrors['overtime-rate'] && (
                  <div className="validation-error mt-1 text-sm text-red-500" tabIndex={-1}>
                    {incomeValidationErrors['overtime-rate']}
                  </div>
                )}
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold text-graphite-grey sm:text-sm">
                  Number of Overtime Hours
                </label>
                <input
                  type="number"
                  step="any"
                  min="0"
                  name="overtime-amount"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-royal-indigo sm:px-4"
                  placeholder="0"
                  value={incomeValues['overtime-amount'] ?? ''}
                  onChange={(evt) => updateIncomeField('overtime-amount', evt.target.value)}
                  onKeyDown={(evt) => {
                    blockNegativeNumberKeys(evt)
                    if (evt.key === 'Enter') {
                      submitCalculator()
                    }
                  }}
                  onFocus={(evt) => scrollIntoView(evt.target)}
                />
                {incomeValidationErrors['overtime-amount'] && (
                  <div className="validation-error mt-1 text-sm text-red-500" tabIndex={-1}>
                    {incomeValidationErrors['overtime-amount']}
                  </div>
                )}
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold text-graphite-grey sm:text-sm">
                  Contract Weekly Stipends ($)
                </label>
                <input
                  type="number"
                  step=".01"
                  min="0"
                  name="contracted-weekly-stipends"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-royal-indigo sm:px-4"
                  placeholder="0.00"
                  value={incomeValues['contracted-weekly-stipends'] ?? ''}
                  onChange={(evt) => updateIncomeField('contracted-weekly-stipends', evt.target.value)}
                  onKeyDown={(evt) => {
                    blockNegativeNumberKeys(evt)
                    if (evt.key === 'Enter') {
                      submitCalculator()
                    }
                  }}
                  onFocus={(evt) => scrollIntoView(evt.target)}
                />
                {incomeValidationErrors['contracted-weekly-stipends'] && (
                  <div className="validation-error mt-1 text-sm text-red-500" tabIndex={-1}>
                    {incomeValidationErrors['contracted-weekly-stipends']}
                  </div>
                )}
              </div>

              <CalculatorStateField state={fieldValues.state} onStateChange={updateState} />

              <CalculatorCityAutocomplete
                cityQuery={fieldValues.cityQuery}
                locations={filteredLocations}
                totalLocationCount={locations.length}
                disabled={!isDateValid}
                loading={locationsLoading}
                isCityValid={isCityValid}
                helperText={locationsError || undefined}
                validationError={validationErrors.city}
                onCityQueryChange={updateCityQuery}
                onCitySelect={selectCity}
                onCityBlur={calculatorForm.validateCityOnBlur}
              />
            </div>

            {submitErrorMessage.length > 0 && (
              <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-red-600">
                {submitErrorMessage}
              </div>
            )}

            <button
              type="button"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-orange py-3 text-sm font-semibold text-white shadow-md transition-all hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50 sm:mt-8 sm:text-base"
              onClick={() => submitCalculator()}
              disabled={isLoading || !canSubmit || hasNegativeIncomeValues}
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" />
                  <span>Calculating...</span>
                </>
              ) : (
                'Calculate'
              )}
            </button>
          </div>

          {showCalculation && calculation && resultContext && (
            <div className="mt-8 space-y-6">
              <p className="text-center text-sm text-gray-600">
                Results for {formatCalculatorResultContext(resultContext)}
              </p>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="rounded-lg border-2 border-gray-200 bg-white p-5 shadow-sm sm:p-6">
                  <h3 className="mb-3 text-lg font-bold text-graphite-grey">Contract Says...</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between border-b border-gray-200 pb-3">
                      <span className="text-gray-600">Weekly Taxable:</span>
                      <span className="font-semibold text-graphite-grey">${calculation?.weeklyTaxable}</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-gray-200 pb-3">
                      <span className="text-gray-600">Weekly Stipends:</span>
                      <span className="font-semibold text-graphite-grey">${contractedWeeklyStipends}</span>
                    </div>
                    <div className="flex items-center justify-between pt-2">
                      <span className="text-base font-semibold text-graphite-grey sm:text-lg">Weekly Total:</span>
                      <span className="text-xl font-bold text-orange sm:text-2xl">
                        ${calculation.weeklyTaxable + contractedWeeklyStipends}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border-2 border-aqua-mint bg-aqua-mint p-5 shadow-sm sm:p-6">
                  <h3 className="mb-3 text-lg font-bold text-white">GSA Max...</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between border-b border-white border-opacity-30 pb-3">
                      <span className="text-white">Weekly Taxable:</span>
                      <span className="font-semibold text-white">${calculation?.weeklyTaxable}</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-white border-opacity-30 pb-3">
                      <span className="text-white">Weekly Stipends:</span>
                      <span className="font-semibold text-white">
                        {calculation &&
                          (typeof calculation.expectedStipends.totalStipends !== 'number'
                            ? calculation.expectedStipends.totalStipends
                            : `$${calculation.expectedStipends.totalStipends}`)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2">
                      <span className="text-base font-semibold text-white sm:text-lg">Weekly Total:</span>
                      <span className="text-xl font-bold text-white sm:text-2xl">{calculation?.totalCompensation}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-5 sm:p-6">
                <h3 className="mb-2 text-lg font-bold text-graphite-grey">Expected Stipends Breakdown</h3>
                <p className="mb-6 text-sm text-gray-600">
                  GSA max rates for {formatCalculatorResultContext(resultContext)} are
                </p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <div className="mb-1 text-sm text-gray-600">Lodging</div>
                    <div className="text-xl font-bold text-graphite-grey sm:text-2xl">
                      {calculation &&
                        (typeof calculation.expectedStipends.lodgingRatePerDay !== 'number'
                          ? `$${Number(calculation.expectedStipends.lodgingRatePerDay) * 7}`
                          : `$${calculation.expectedStipends.lodgingRatePerDay * 7}`)}
                      <span className="ml-2 text-xs font-normal text-gray-500 sm:text-sm">/ week</span>
                    </div>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <div className="mb-1 text-sm text-gray-600">Meals</div>
                    <div className="text-xl font-bold text-graphite-grey sm:text-2xl">
                      {calculation &&
                        (typeof calculation.expectedStipends.mealsRatePerDay !== 'number'
                          ? `$${Number(calculation.expectedStipends.mealsRatePerDay) * 7}`
                          : `$${calculation.expectedStipends.mealsRatePerDay * 7}`)}
                      <span className="ml-2 text-xs font-normal text-gray-500 sm:text-sm">/ week</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="calculator-focus-trap" tabIndex={-1}></div>
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default IncomeCalc
